import React, { useMemo, useState } from "react";
import { Shield, Search, Clock, User, Database, Eye, ChevronDown, ChevronUp, SlidersHorizontal } from "lucide-react";
import Wrapper from "../../../components/Wrapper";

export default function AuditLogPage() {
  const [search, setSearch] = useState("");
  const [filterTable, setFilterTable] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [filterWorker, setFilterWorker] = useState("all");
  const [expandedLog, setExpandedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [workers] = useState([
    { id: "w1", name: "Admin A" },
    { id: "w2", name: "Admin B" },
    { id: "w3", name: "Supervisor C" },
  ]);
  const [logs] = useState([
    {
      id: "l0",
      table_name: "work_logs",
      action: "INSERT",
      changed_by: "w1",
      changed_by_name: "Shruti",
      notes: "Work log insert by Shruti",
      created_at: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
      old_data: null,
      new_data: {
        notes: null,
        end_time: "17:55",
        work_date: "2026-04-08",
        worker_id: "d15e0d06-0cf7-40b0-a443-9bc9871c3a21",
        reagent_id: "258654d3-d8ea-4a2d-97ef-2f6b3bc45747",
        start_time: "16:11",
        quantity_completed: 2,
      },
      user_agent:
        "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/123.0 Safari/537.36",
    },
    {
      id: "l1",
      table_name: "orders",
      action: "UPDATE",
      changed_by: "w1",
      changed_by_name: "Admin A",
      notes: "Updated order status and due date",
      created_at: new Date().toISOString(),
      old_data: { order_number: "SD260406006", status: "pending", due_date: "2026-04-14", customer_name: "Mehul" },
      new_data: { order_number: "SD260406006", status: "in_progress", due_date: "2026-04-16", customer_name: "Mehul" },
    },
    {
      id: "l2",
      table_name: "reagent_stock",
      action: "INSERT",
      changed_by: "w2",
      changed_by_name: "Admin B",
      notes: "Added stock batch for NO3 Reagent",
      created_at: new Date(Date.now() - 3600 * 1000).toISOString(),
      old_data: null,
      new_data: { reagent_name: "NO3 Reagent", quantity: 50, batch_no: "B-0426-01", unit_cost: 120, expires_at: "2027-04-01" },
    },
    {
      id: "l3",
      table_name: "work_logs",
      action: "UPDATE",
      changed_by: "w3",
      changed_by_name: "Supervisor C",
      notes: "Corrected completed quantity",
      created_at: new Date(Date.now() - 2 * 3600 * 1000).toISOString(),
      old_data: { worker_name: "Worker A", quantity_completed: 85, work_date: "2026-04-09", start_time: "09:00", end_time: "16:00" },
      new_data: { worker_name: "Worker A", quantity_completed: 95, work_date: "2026-04-09", start_time: "09:00", end_time: "16:00" },
    },
  ]);

  const getTableLabel = (table) => {
    const labels = {
      work_logs: "Work Logs",
      targets: "Targets",
      reagent_stock: "Stock",
      orders: "Orders",
      order_items: "Order Items",
      workers: "Crew",
      reagents: "Reagents",
      attendance: "Attendance",
      daily_summaries: "Daily Summaries",
    };
    return labels[table] || table;
  };

  const filteredLogs = useMemo(
    () =>
      logs.filter((l) => {
        const matchesSearch = (l.table_name + l.changed_by_name + l.notes).toLowerCase().includes(search.toLowerCase());
        const matchesTable = filterTable === "all" || l.table_name === filterTable;
        const matchesAction = filterAction === "all" || l.action === filterAction;
        const matchesWorker = filterWorker === "all" || l.changed_by === filterWorker;
        return matchesSearch && matchesTable && matchesAction && matchesWorker;
      }),
    [logs, search, filterTable, filterAction, filterWorker]
  );
  const uniqueTables = [...new Set(logs.map((l) => l.table_name))];
  const activeFilters = [filterTable, filterAction, filterWorker].filter((f) => f !== "all").length;

  const actionBadge = (a) => (a === "INSERT" ? "bg-emerald-100 text-emerald-700" : a === "UPDATE" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700");

  const renderDataDiff = (oldData, newData) => {
    if (!oldData && !newData) return null;
    const allKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);
    const changes = [];
    allKeys.forEach((key) => {
      if (key === "created_at" || key === "id") return;
      const oldVal = oldData?.[key];
      const newVal = newData?.[key];
      const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
      changes.push({ key, old: oldVal, new: newVal, changed });
    });
    return (
      <div className="space-y-2 text-xs sm:text-sm">
        {changes.map(({ key, old, new: newVal, changed }) => (
          <div key={key} className={`p-2 rounded ${changed ? "bg-amber-50" : "bg-slate-50"}`}>
            <span className="font-medium text-slate-600 break-all">{key}:</span>
            {changed ? (
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-2">
                {old !== undefined && <span className="text-red-600 line-through break-all">{typeof old === "object" ? JSON.stringify(old) : String(old)}</span>}
                <span className="text-emerald-600 break-all">{typeof newVal === "object" ? JSON.stringify(newVal) : String(newVal)}</span>
              </div>
            ) : (
              <span className="ml-2 text-slate-700 break-all">{typeof newVal === "object" ? JSON.stringify(newVal) : String(newVal ?? old)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Wrapper>
      <div className="space-y-4 sm:space-y-6 pb-20 lg:pb-6">
        <div className="w-full text-left">
          <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Audit Log</h2>
          <p className="mt-1 text-slate-500 text-left">Track all data changes for compliance</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Database className="text-emerald-600" size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-slate-800">{logs.length}</p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Total Events</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="text-blue-600" size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-slate-800">
                  {logs.filter((l) => l.action === "UPDATE").length}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Updates</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="text-amber-600" size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-slate-800">
                  {logs.filter((l) => new Date(l.created_at).toDateString() === new Date().toDateString()).length}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Today</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="text-red-600" size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-slate-800">
                  {new Set(logs.map((l) => l.changed_by_name)).size}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 truncate">Active Users</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search logs..."
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm sm:text-base"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 border rounded-lg transition-colors ${showFilters || activeFilters > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-slate-200 hover:bg-slate-50"}`}
              >
                <SlidersHorizontal size={18} />
                <span className="hidden sm:inline">Filters</span>
                {activeFilters > 0 && (
                  <span className="w-5 h-5 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center">
                    {activeFilters}
                  </span>
                )}
              </button>
            </div>
            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100">
                <select value={filterTable} onChange={(e) => setFilterTable(e.target.value)} className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                  <option value="all">All Tables</option>
                  {uniqueTables.map((table) => (
                    <option key={table} value={table}>
                      {getTableLabel(table)}
                    </option>
                  ))}
                </select>
                <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                  <option value="all">All Actions</option>
                  <option value="INSERT">Insert</option>
                  <option value="UPDATE">Update</option>
                  <option value="DELETE">Delete</option>
                </select>
                <select value={filterWorker} onChange={(e) => setFilterWorker(e.target.value)} className="flex-1 px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm">
                  <option value="all">All Users</option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="mx-auto text-slate-300" size={48} />
              <p className="mt-4 text-slate-500 text-sm sm:text-base">No audit logs found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredLogs.map((log) => {
                const isExpanded = expandedLog === log.id;
                const time = new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                const date = new Date(log.created_at).toLocaleDateString();
                
                return (
                  <div key={log.id} className="hover:bg-slate-50 transition-colors">
                    <div
                      className="p-3 sm:p-4 cursor-pointer active:bg-slate-100"
                      onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                    >
                      <div className="flex items-start sm:items-center justify-between gap-2">
                        <div className="flex items-start sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                            <Database className="text-slate-500" size={16} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${actionBadge(log.action)}`}>
                                {log.action}
                              </span>
                              <span className="font-medium text-slate-800 text-sm sm:text-base truncate">
                                {getTableLabel(log.table_name)}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">
                              {log.changed_by_name} - {log.notes}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                          <div className="text-right text-xs sm:text-sm hidden sm:block">
                            <p className="text-slate-800">{date}</p>
                            <p className="text-slate-500">{time}</p>
                          </div>
                          <div className="text-right text-xs sm:text-sm sm:hidden">
                            <p className="text-slate-500">{time}</p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp size={18} className="text-slate-400" />
                          ) : (
                            <ChevronDown size={18} className="text-slate-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-slate-100 bg-slate-50">
                        <div className="pt-3 sm:pt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Eye size={16} className="text-slate-500" />
                            <span className="font-medium text-slate-700 text-sm">Data Changes</span>
                          </div>
                          {renderDataDiff(log.old_data, log.new_data)}
                        {log.user_agent && (
                          <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-white rounded-lg">
                            <p className="text-xs text-slate-500 break-all">
                              <strong>Browser:</strong> {log.user_agent}
                            </p>
                          </div>
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
